import { localizePhoneNumbers } from '@lucid/gatsby-theme-lucid';
import { Box, Heading } from 'lucid-components-react';
import React, { useContext } from 'react';

import Link from 'components/link/link';
import LayoutContext from 'contexts/layoutContext';
import {
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphMarketoForm,
  Drupal_ParagraphOrCallCta
} from 'generated/graphql';
import useMarketoLink from 'hooks/use-marketo-link';
import { Text } from 'theme/typography';

interface Props {
  header?: string | null;
  subheader?: string | null;
  pitch?: string | null;
  ctas?: Drupal_ParagraphCtaButton[] | null;
  orCallCta?: Drupal_ParagraphOrCallCta | null;
  variant?: 'dark' | 'light';
}

const PreFooterCta = (props: Props) => {
  const { language } = useContext(LayoutContext);
  const { header, subheader, pitch, ctas, orCallCta, variant = 'dark' } = props;
  const isPhoneNumber = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im;
  const getMarketoLink = useMarketoLink(language);

  return (
    <Box
      css={theme => ({
        ...(variant === 'dark' && {
          backgroundColor: theme.colors.coolGrey[80]
        })
      })}
      pb={8}
      pt={5}
    >
      <Box
        css={{
          maxWidth: '32rem',
          margin: '0 auto',
          textAlign: 'center'
        }}
        px={4}
      >
        {header && (
          <Heading as="h2" variant="subheadM">
            {isPhoneNumber.test(header) ? (
              <Link
                css={theme => ({
                  fontWeight: theme.fontWeights.bolder,
                  ...(variant === 'dark' && { color: theme.colors.white }),
                  '&:hover': {
                    color:
                      variant === 'dark'
                        ? theme.colors.white
                        : theme.colors.coolGrey[100]
                  }
                })}
                to={localizePhoneNumbers(`tel:+1${header}`)}
              >
                {localizePhoneNumbers(header)}
              </Link>
            ) : (
                localizePhoneNumbers(header)
              )}
          </Heading>
        )}

        {subheader && (
          <Text
            css={(theme: any) => ({
              ...(variant === 'dark' && {
                color: theme.colors.white
              })
            })}
            textStyles="bodyBold"
          >
            {subheader}
          </Text>
        )}

        {pitch && (
          <Text
            css={(theme: any) => ({
              ...(variant === 'dark' && {
                color: theme.colors.white
              })
            })}
            textStyles="bodySmall"
          >
            {pitch}
          </Text>
        )}

        {/* TODO use ButtonGroup when it is available */}
        <Box>
          {ctas &&
            ctas.map(
              (
                {
                  fieldButtonLink,
                  fieldButtonText,
                  fieldIsSecondary,
                  fieldMarketo
                },
                i
              ) => {
                const marketoForm = fieldMarketo
                  ? (fieldMarketo.entity as Drupal_ParagraphMarketoForm)
                  : null;
                if (fieldButtonLink) {
                  return (
                    <Link
                      key={fieldButtonText}
                      asButton
                      css={theme => ({
                        ...(i + 1 < ctas.length && {
                          marginRight: theme.space[3]
                        })
                      })}
                      primary={!fieldIsSecondary}
                      secondary={!!fieldIsSecondary}
                      to={fieldButtonLink}
                    >
                      {fieldButtonText}
                    </Link>
                  );
                }
                if (marketoForm) {
                  const { url, copy } = getMarketoLink(
                    marketoForm.fieldMarketoId
                  )!;
                  return (
                    <Link
                      key={fieldButtonText}
                      asButton
                      css={theme => ({
                        ...(i + 1 < ctas.length && {
                          marginRight: theme.space[3]
                        })
                      })}
                      primary={!fieldIsSecondary}
                      secondary={!!fieldIsSecondary}
                      to={url}
                    >
                      {fieldButtonText || copy}
                    </Link>
                  );
                }
                return;
              }
            )}

          {orCallCta && (
            <Link
              className="pre-footer-cta__ctas-phone-link"
              to={`tel:+1${orCallCta.fieldCallCtaPhoneNumber}`}
            >
              {orCallCta.fieldCallCtaText}
              {orCallCta.fieldCallCtaPhoneNumber}
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PreFooterCta;
